
import { supabase } from "@/integrations/supabase/client";
import { Subscription, SubscriptionService } from "@/lib/data";

// Helper function to get services related to a subscription
export async function getSubscriptionServices(subscriptionId: string): Promise<SubscriptionService[]> {
  try {
    const { data, error } = await supabase
      .from('subscription_services')
      .select(`
        *,
        service:services(*)
      `)
      .eq('subscription_id', subscriptionId);
    
    if (error) {
      console.error('Error fetching subscription services:', error);
      throw error;
    }
    
    if (!data) return [];
    
    return data.map(ss => ({
      id: ss.id,
      subscription_id: ss.subscription_id,
      service_id: ss.service_id,
      service: {
        service_id: ss.service.service_id,
        service_name: ss.service.service_name,
        default_price: ss.service.default_price,
        description: ss.service.description || '',
        parent_service_id: ss.service.parent_service_id
      },
      status: ss.status as "registered" | "active" | "cancelled" | "terminated",
      registration_date: ss.registration_date,
      activation_date: ss.activation_date,
      termination_date: ss.termination_date,
      cancellation_date: ss.cancellation_date,
      price: ss.price || ss.service.default_price,
      last_bill_date: ss.last_bill_date,
      next_bill_date: ss.next_bill_date
    }));
  } catch (error) {
    console.error('Error in getSubscriptionServices:', error);
    return [];
  }
}

// Optimized function to get all subscriptions with batched service fetching
export async function getSubscriptions(context: any): Promise<Subscription[]> {
  try {
    // Support both direct customerId parameter and queryKey parameter from React Query
    const customerId = typeof context === 'string' ? context : 
                      Array.isArray(context?.queryKey) && context.queryKey[1] ? context.queryKey[1] : undefined;
    
    // Avoid making unnecessary fetch calls
    if (context?.meta?.preventFetch) {
      console.log('Fetch prevented by meta flag');
      return [];
    }
    
    let query = supabase
      .from('subscriptions')
      .select(`
        *,
        customer:customers(*)
      `);
    
    if (customerId) {
      query = query.eq('customer_id', customerId);
    }
    
    const { data: subscriptionsData, error } = await query;
    
    if (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }

    // If no subscriptions were found, return empty array
    if (!subscriptionsData || subscriptionsData.length === 0) {
      return [];
    }

    // Extract subscription IDs for service fetching
    const subscriptionIds = subscriptionsData.map(sub => sub.subscription_id);
    
    // Fetch all services for all subscriptions in one query only if there are subscriptions
    let allServices: Record<string, SubscriptionService[]> = {};
    
    if (subscriptionIds.length > 0) {
      try {
        const { data: servicesData, error: servicesError } = await supabase
          .from('subscription_services')
          .select(`
            *,
            service:services(*)
          `)
          .in('subscription_id', subscriptionIds);
          
        if (servicesError) {
          console.error('Error fetching subscription services in bulk:', servicesError);
          // Continue with empty services rather than failing completely
          allServices = {};
        } else if (servicesData) {
          // Group services by subscription_id
          allServices = servicesData.reduce((acc, service) => {
            if (!acc[service.subscription_id]) {
              acc[service.subscription_id] = [];
            }
            
            acc[service.subscription_id].push({
              id: service.id,
              subscription_id: service.subscription_id,
              service_id: service.service_id,
              service: {
                service_id: service.service.service_id,
                service_name: service.service.service_name,
                default_price: service.service.default_price,
                description: service.service.description || '',
                parent_service_id: service.service.parent_service_id
              },
              status: service.status as "registered" | "active" | "cancelled" | "terminated",
              registration_date: service.registration_date,
              activation_date: service.activation_date,
              termination_date: service.termination_date,
              cancellation_date: service.cancellation_date,
              price: service.price || service.service.default_price,
              last_bill_date: service.last_bill_date,
              next_bill_date: service.next_bill_date
            });
            
            return acc;
          }, {} as Record<string, SubscriptionService[]>);
        }
      } catch (error) {
        console.error('Error processing subscription services:', error);
        // Continue with empty services rather than failing completely
        allServices = {};
      }
    }
    
    // Map the subscriptions data with their services
    const subscriptions = subscriptionsData.map(sub => {
      const services = allServices[sub.subscription_id] || [];
      
      // Handle potentially missing customer data
      const customer = sub.customer ? {
        id: sub.customer.id,
        customer_name: sub.customer.customer_name,
        customer_number: sub.customer.customer_number,
        email: sub.customer.email || '',
        phone: sub.customer.phone || '',
        customer_type: sub.customer.customer_type as 'private' | 'business' || 'private',
        postal_street: sub.customer.postal_street || '',
        postal_zip_code: sub.customer.postal_zip_code || '',
        postal_city: sub.customer.postal_city || '',
        created_at: sub.customer.created_at,
        updated_at: sub.customer.updated_at,
        first_name: sub.customer.first_name || '',
        last_name: sub.customer.last_name || ''
      } : null;
      
      return {
        subscription_id: sub.subscription_id,
        subscription_number: sub.subscription_number,
        customer_id: sub.customer_id,
        customer,
        status: sub.status as "registered" | "active" | "terminated" | "cancelled",
        services,
        registered_at: sub.registered_at,
        activation_date: sub.activation_date,
        termination_date: sub.termination_date,
        cancellation_date: sub.cancellation_date,
        next_bill_date: sub.next_bill_date,
        last_bill_date: sub.last_bill_date,
        installation_street: sub.installation_street,
        installation_zip_code: sub.installation_zip_code,
        installation_city: sub.installation_city,
        housing_type: sub.housing_type as "house" | "apartment" | "office",
        updated_at: sub.updated_at,
        created_at: sub.created_at,
        alarm_com_id: sub.alarm_com_id,
        created_by: sub.created_by,
        invoice_reference: sub.invoice_reference,
        contact_name: sub.contact_name,
        contact_phone: sub.contact_phone,
        contact_email: sub.contact_email,
        prom_avarn: sub.prom_avarn,
      };
    });
    
    return subscriptions;
  } catch (error) {
    console.error('Error in getSubscriptions:', error);
    // Return empty array instead of rethrowing to avoid infinite retry loops
    return [];
  }
}

// Get a single subscription by ID
export async function getSubscriptionById(id: string): Promise<Subscription | null> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        customer:customers(*)
      `)
      .eq('subscription_id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching subscription:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    const services = await getSubscriptionServices(data.subscription_id);
    
    // Handle potentially missing customer data
    const customer = data.customer ? {
      id: data.customer.id,
      customer_name: data.customer.customer_name,
      customer_number: data.customer.customer_number,
      email: data.customer.email || '',
      phone: data.customer.phone || '',
      customer_type: data.customer.customer_type as 'private' | 'business' || 'private',
      postal_street: data.customer.postal_street || '',
      postal_zip_code: data.customer.postal_zip_code || '',
      postal_city: data.customer.postal_city || '',
      created_at: data.customer.created_at,
      updated_at: data.customer.updated_at,
      first_name: data.customer.first_name || '',
      last_name: data.customer.last_name || ''
    } : null;
    
    return {
      subscription_id: data.subscription_id,
      subscription_number: data.subscription_number,
      customer_id: data.customer_id,
      customer,
      status: data.status as "registered" | "active" | "terminated" | "cancelled",
      registered_at: data.registered_at,
      activation_date: data.activation_date,
      termination_date: data.termination_date,
      cancellation_date: data.cancellation_date,
      alarm_com_id: data.alarm_com_id,
      installation_street: data.installation_street,
      installation_zip_code: data.installation_zip_code,
      installation_city: data.installation_city,
      created_by: data.created_by,
      housing_type: data.housing_type as "house" | "apartment" | "office",
      invoice_reference: data.invoice_reference,
      last_bill_date: data.last_bill_date,
      next_bill_date: data.next_bill_date,
      created_at: data.created_at,
      updated_at: data.updated_at,
      contact_name: data.contact_name,
      contact_phone: data.contact_phone,
      contact_email: data.contact_email,
      prom_avarn: data.prom_avarn,
      services
    };
  } catch (error) {
    console.error('Error in getSubscriptionById:', error);
    return null;
  }
}

// Get subscriptions by customer ID
export async function getSubscriptionsByCustomerId(customerId: string): Promise<Subscription[]> {
  return getSubscriptions(customerId);
}
